import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import { Avatar, Box, Button, Modal, Typography } from "@mui/material";
import debounce from "debounce";
import React, { useEffect, useRef, useState } from "react";
import AlertSnackbar from "../../common/AlertSnackbar";
import { useAllUsers } from "../../common/contexts/allUsersContext";
import { useUser } from "../../common/contexts/userContext";
import {
    acceptLeagueRequest,
    deleteLeague,
    getLeagueById,
    getLeagueRequests,
    rejectLeagueRequest,
    uploadLeagueImage,
} from "../../services/leagueService";
import { palette } from "../../styles/palette";
import { League } from "../../types/League";
import { RequestLeague } from "../../types/RequestLeague";
import "./LeagueStyles.scss";
import { LeagueViewTab } from "./MobileLeagues";
import RemoveUsersPanel from "./RemoveUsersPanel";

interface ManageLeaguesPanelProps {
  manageLeagueId: number;
  isDesktop?: boolean;
  setCurrentView: React.Dispatch<React.SetStateAction<LeagueViewTab>>;
}

export { getLeagueRequests };

const ManageLeaguesPanel: React.FC<ManageLeaguesPanelProps> = ({
  manageLeagueId,
  isDesktop,
  setCurrentView,
}) => {
  const [userRequests, setUserRequests] = useState<RequestLeague[]>([]);
  const [refetchData, setRefetchData] = useState<boolean>(false);
  const [currentLeague, setCurrentLeague] = useState<League | undefined>(
    undefined
  );
  const { users } = useAllUsers();
  const { setUser } = useUser();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");
  useEffect(() => {
    getLeagueRequests(manageLeagueId).then(setUserRequests);
  }, [manageLeagueId]);

  useEffect(() => {
    getLeagueById(manageLeagueId).then(setCurrentLeague);
  }, [manageLeagueId]);

  function correctDelete() {
    setUser((prev) => ({
      ...prev,
      leagues: prev.leagues.filter((item) => item.id !== currentLeague?.id),
    }));
    setShowDeleteModal(false);
    setCurrentView("YourLeagues");
  }
  async function handleDelete() {
    if (currentLeague) {
      deleteLeague(currentLeague.id)
        .then(correctDelete)
        .catch(() => alert("Something went wrong"));
    } else {
      setShowDeleteModal(false);
      alert("League cannot be deleted");
    }
  }

  function withCacheBust(url: string) {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${Date.now()}`;
  }

  function validateImageType(fileName: string) {
    const extension = fileName.substring(fileName.lastIndexOf(".") + 1).toLowerCase();
    return extension === "jpg" || extension === "jpeg" || extension === "png";
  }

  async function handleLeagueImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const image = event.target.files?.[0];
    event.target.value = "";
    setUploadError("");

    if (!image || !currentLeague) {
      return;
    }

    if (!validateImageType(image.name)) {
      setUploadError("Only JPG/JPEG/PNG files are allowed.");
      return;
    }

    try {
      setUploadingImage(true);
      const response = await uploadLeagueImage(currentLeague.id, image);
      const refreshedUrl = withCacheBust(response.leagueImageUrl);
      setCurrentLeague((prev) =>
        prev ? { ...prev, leagueImageUrl: refreshedUrl } : prev
      );
      setUser((prev) => ({
        ...prev,
        leagues: prev.leagues.map((league) =>
          league.id === currentLeague.id
            ? { ...league, leagueImageUrl: refreshedUrl }
            : league
        ),
      }));
    } catch {
      setUploadError(
        "Image upload failed. Please check your file and try again."
      );
    } finally {
      setUploadingImage(false);
    }
  }

  return (
    <div className={`manageLeague__root${isDesktop ? " manageLeague__root--desktop" : ""}`}>
      <div className="manageLeague__header">
        <button
          className="manageLeague__back"
          onClick={() => setCurrentView("YourLeagues")}
        >
          <ArrowBackRoundedIcon />
        </button>
        <Avatar
          src={currentLeague?.leagueImageUrl}
          className="manageLeague__headerAvatar"
        >
          {currentLeague?.name?.charAt(0)}
        </Avatar>
        <div className="manageLeague__headerInfo">
          <h2 className="manageLeague__name">{currentLeague?.name}</h2>
          <span className="manageLeague__meta">
            <GroupsRoundedIcon />
            {currentLeague?.usersCount ?? 0}{" "}
            {currentLeague?.usersCount === 1 ? "player" : "players"}
          </span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <Button
              startIcon={<CloudUploadIcon />}
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploadingImage}
              sx={{ textTransform: "none" }}
            >
              {uploadingImage ? "Uploading..." : "Replace league image"}
            </Button>
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              onChange={handleLeagueImageUpload}
              style={{ display: "none" }}
            />
          </div>
          {uploadError && (
            <Typography color="error" sx={{ mt: 1, fontSize: "0.85rem" }}>
              {uploadError}
            </Typography>
          )}
        </div>
        <Button
          className="manageLeague__deleteBtn"
          startIcon={<DeleteOutlineRoundedIcon />}
          onClick={() => setShowDeleteModal(true)}
        >
          Delete league
        </Button>
      </div>

      <div className="manageLeague__grid">
        <section className="manageLeague__panel">
          <h3 className="manageLeague__panelTitle">
            Pending requests
            {userRequests.length > 0 && (
              <span className="manageLeague__countBadge">
                {userRequests.length}
              </span>
            )}
          </h3>
          <div className="manageLeague__requestsList">
            {userRequests.map((req) => {
              const currUser = users.filter((item) => item.id === req.userId)[0];
              return (
                currUser && (
                  <UserTile
                    key={`${currUser.id} tile in req`}
                    name={currUser.username}
                    avatarUrl={currUser.avatarUrl}
                    userId={req.userId}
                    manageLeagueId={manageLeagueId}
                    setUserRequests={setUserRequests}
                    setRefetchData={setRefetchData}
                  />
                )
              );
            })}
            {userRequests.length === 0 && (
              <Typography className="manageLeague__emptyText">
                No requests awaiting acceptance.
              </Typography>
            )}
          </div>
        </section>

        <section className="manageLeague__panel">
          <h3 className="manageLeague__panelTitle">Members</h3>
          <RemoveUsersPanel
            manageLeagueID={manageLeagueId}
            refetchData={refetchData}
            isDesktop={isDesktop}
          />
        </section>
      </div>

      <ModalDelete
        open={showDeleteModal}
        onClose={setShowDeleteModal}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default ManageLeaguesPanel;

interface UserTileProps {
  name: string;
  avatarUrl: string;
  userId: string;
  manageLeagueId: number;
  setUserRequests: React.Dispatch<React.SetStateAction<RequestLeague[]>>;
  setRefetchData: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserTile: React.FC<UserTileProps> = ({
  name,
  avatarUrl,
  userId,
  manageLeagueId,
  setUserRequests,
  setRefetchData,
}) => {
  const [showAlertAdd, setShowAlertAdd] = useState(false);
  const [showAlertRemove, setShowAlertRemove] = useState(false);
  function addUserToLeague() {
    acceptLeagueRequest(manageLeagueId, userId)
      .then(() => {
        setUserRequests((prev) => prev.filter((item) => item.userId !== userId));
        setShowAlertAdd(true);
        setRefetchData((prev) => !prev);
      })
      .catch(() => alert("Something went wrong - try again."));
  }

  function removeUserRequest() {
    rejectLeagueRequest(manageLeagueId, userId)
      .then(() => {
        setShowAlertRemove(true);
        setUserRequests((prev) => prev.filter((item) => item.userId !== userId));
      })
      .catch(() => alert("Something went wrong"));
  }
  return (
    <div className="userTile__root">
      <div className="userTile__left">
        <Avatar src={avatarUrl} className="userTile__avatar">
          {name?.charAt(0)}
        </Avatar>
        <span className="userTile__name">{name}</span>
      </div>
      <div className="userTile__actions">
        <button
          className="userTile__action userTile__action--reject"
          aria-label="Reject request"
          onClick={debounce(removeUserRequest, 500)}
        >
          <CancelOutlinedIcon />
        </button>
        <button
          className="userTile__action userTile__action--accept"
          aria-label="Accept request"
          onClick={debounce(addUserToLeague, 500)}
        >
          <CheckCircleOutlineIcon />
        </button>
      </div>
      {showAlertAdd && (
        <AlertSnackbar
          severity="success"
          message="User added successfully!"
          key={`${name}-tile-alert-add`}
        />
      )}
      {showAlertRemove && (
        <AlertSnackbar
          severity="info"
          message="User requests removed successfully!"
          key={`${name}-tile-alert-remove`}
        />
      )}
    </div>
  );
};

const deleteModalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(380px, calc(100vw - 32px))",
  bgcolor: palette.bgPrimary,
  color: palette.textPrimary,
  border: `1px solid ${palette.border}`,
  borderRadius: "20px",
  boxShadow: "var(--elevation-3)",
  p: "28px 24px 22px",
  textAlign: "center" as "center",
  outline: "none",
};

interface ModalDeleteProps {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  handleDelete: () => void;
}

export const ModalDelete: React.FC<ModalDeleteProps> = ({
  open,
  onClose,
  handleDelete,
}) => (
  <Modal
    open={open}
    onClose={() => onClose(false)}
    aria-labelledby="delete-league-title"
    aria-describedby="delete-league-description"
  >
    <Box sx={deleteModalStyle}>
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: "auto",
          borderRadius: "9999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(229, 77, 77, 0.14)",
        }}
      >
        <DeleteOutlineRoundedIcon
          sx={{ fontSize: 28, color: palette.errorReject }}
        />
      </Box>

      <Typography
        id="delete-league-title"
        sx={{ fontSize: "1.25rem", fontWeight: 700, mt: "16px" }}
      >
        Delete this league?
      </Typography>
      <Typography
        id="delete-league-description"
        sx={{ color: palette.textSecondary, fontSize: "0.9rem", mt: "8px" }}
      >
        This permanently removes the league and its standings for every member.
        This can't be undone.
      </Typography>

      <Box sx={{ display: "flex", gap: "12px", mt: "24px" }}>
        <Button
          fullWidth
          onClick={() => onClose(false)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "9999px",
            py: "9px",
            color: palette.textPrimary,
            border: `1px solid ${palette.border}`,
            "&:hover": {
              background: palette.bgElevated,
              border: `1px solid ${palette.hoverBorder}`,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          onClick={() => handleDelete()}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "9999px",
            py: "9px",
            color: "#fff",
            background: palette.errorReject,
            "&:hover": {
              background: palette.errorReject,
              filter: "brightness(0.92)",
            },
          }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Modal>
);

export const mockUserData = [
  { name: "user1", avatarUrl: "./images/test_avatar.jpg", id: 1 },
  { name: "user2", avatarUrl: "./images/test_avatar.jpg", id: 2 },
  { name: "user3", avatarUrl: "./images/test_avatar.jpg", id: 3 },
  { name: "user4", avatarUrl: "./images/test_avatar.jpg", id: 4 },
  { name: "user5", avatarUrl: "./images/test_avatar.jpg", id: 5 },
  { name: "user6", avatarUrl: "./images/test_avatar.jpg", id: 6 },
  { name: "user7", avatarUrl: "./images/test_avatar.jpg", id: 7 },
  { name: "user8", avatarUrl: "./images/test_avatar.jpg", id: 8 },
  { name: "user9", avatarUrl: "./images/test_avatar.jpg", id: 9 },
];
